import './AccountSettings.css';
import { Context } from '../Context';
import { useEffect, useState, useContext } from 'react';

import { 
    ImCheckboxUnchecked, 
    ImCheckboxChecked
} from "react-icons/im";
import { 
    FaUser, 
    FaShieldAlt, 
    FaLock, 
    FaEye, 
    FaEyeSlash 
} from "react-icons/fa";

const AccountSettings = ({activeUser}) => {
    const contextObject = useContext(Context);

    const [changingPassword, setChangingPassword] = useState(false);
    const [currentPasswordInput, setCurrentPasswordInput] = useState('');
    const [newPassword1, setNewPassword1] = useState('');
    const [newPassword2, setNewPassword2] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [clickAble, setClickAble] = useState(false);
    const [noMatchWarning, setNoMatchWarning] = useState(false);
    const [wrongPasswordWarning, setWrongPasswordWarning] = useState(false);
    const [successfulPasswordChange, setSuccessfulPasswordChange] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState('');

    // Calculate password strength
    const calculatePasswordStrength = (password) => {
        if (password.length === 0) return '';
        if (password.length < 6) return 'weak';
        
        let score = 0;
        if (password.length >= 8) score++;
        if (/[a-z]/.test(password)) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;
        
        if (score < 2) return 'weak';
        if (score < 3) return 'fair';
        if (score < 4) return 'good';
        return 'strong';
    };

    useEffect(() => {
        const strength = calculatePasswordStrength(newPassword1);
        setPasswordStrength(strength);
        
        if (newPassword2 !== '') {
            if (newPassword1 !== newPassword2) {
                setNoMatchWarning(true);
                setClickAble(false);
            } else {
                setNoMatchWarning(false);
                setClickAble(currentPasswordInput !== '' && newPassword1 !== '' && strength !== 'weak');
            }
        } else {
            setClickAble(false);
        }
    }, [newPassword1, newPassword2, currentPasswordInput]);

    const successfulChange = () => {
        setSuccessfulPasswordChange(true);
        setTimeout(() => {
            contextObject.logoutUser()
        }, 2000)
    };

    const failedChange = (text) => {
        console.log(text);
        setWrongPasswordWarning(true);
    };

    const updatePassword = () => {
        setWrongPasswordWarning(false);
        contextObject.changePassword(currentPasswordInput, newPassword1, successfulChange, failedChange)
    };

    const resetPasswordForm = () => {
        setChangingPassword(false);
        setCurrentPasswordInput('');
        setNewPassword1('');
        setNewPassword2('');
        setNoMatchWarning(false);
        setWrongPasswordWarning(false);
        setSuccessfulPasswordChange(false);
        setPasswordStrength('');
    };

    const privileges = [
        { key: 'vendorPriv', label: 'Vendor Requests' },
        { key: 'customerPriv', label: 'Customer Requests' },
        { key: 'updateRequestPriv', label: 'Update Requests' },
        { key: 'projectBookingPriv', label: 'Project Booking' },
        { key: 'projectBookingReviewPriv', label: 'Project Booking Review' },
        { key: 'convertPriv', label: 'Conversion System' },
        { key: 'companyOverviewPriv', label: 'Company Overview' },
        { key: 'qualityControlPriv', label: 'Quality Control' },
        { key: 'procurementPriv', label: 'Procurement Tracker' },
        { key: 'fieldServicePriv', label: 'Field Service Management' },
        { key: 'myAppointmentsPriv', label: 'My Appointments' },
        { key: 'adminPriv', label: 'System Admin' }
    ];

    const getPasswordStrengthText = () => {
        switch (passwordStrength) {
            case 'weak': return 'Weak';
            case 'fair': return 'Fair';
            case 'good': return 'Good';
            case 'strong': return 'Strong';
            default: return '';
        }
    };

    return(
        <div className='Account-Settings-Window'>
            <div className='Account-Settings-Container'>
                <div className='Account-Settings-Header'>
                    <h1>Account Settings</h1>
                    <p>Manage your account information and security settings</p>
                </div>

                <div className='Account-Settings-Grid'>
                    {/* Personal Information Card */}
                    <div className='Account-Settings-Card'>
                        <div className='Account-Settings-Card-Header'>
                            <FaUser className='Account-Settings-Card-Icon' />
                            <h2 className='Account-Settings-Card-Title'>Personal Information & Security</h2>
                        </div>
                        
                        <div className='Account-Settings-Field'>
                            <label className='Account-Settings-Label'>Full Name</label>
                            <div className='Account-Settings-Value'>
                                {activeUser.name}
                            </div>
                        </div>

                        <div className='Account-Settings-Field'>
                            <label className='Account-Settings-Label'>Email Address</label>
                            <div className='Account-Settings-Value'>
                                {activeUser.email}
                            </div>
                        </div>

                        {/* Password Section */}
                        <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
                            {!changingPassword ? (
                                <div>
                                    <div className='Account-Settings-Field'>
                                        <label className='Account-Settings-Label'>Password</label>
                                        <div className='Account-Settings-Value'>
                                            ••••••••••••
                                        </div>
                                    </div>
                                    <button 
                                        className='Account-Settings-Button Account-Settings-Button-Primary'
                                        onClick={() => setChangingPassword(true)}
                                    >
                                        Update Password
                                    </button>
                                </div>
                            ) : (
                                <div className='Account-Settings-Password-Change'>
                                    <div className='Account-Settings-Field'>
                                        <label className='Account-Settings-Label'>Current Password</label>
                                        <div style={{ position: 'relative' }}>
                                            <input 
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                className='Account-Settings-Input'
                                                value={currentPasswordInput}
                                                onChange={(e) => setCurrentPasswordInput(e.target.value)}
                                                placeholder="Enter your current password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '12px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: '#718096'
                                                }}
                                            >
                                                {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className='Account-Settings-Field'>
                                        <label className='Account-Settings-Label'>New Password</label>
                                        <div style={{ position: 'relative' }}>
                                            <input 
                                                type={showNewPassword ? 'text' : 'password'}
                                                className='Account-Settings-Input'
                                                value={newPassword1}
                                                onChange={(e) => setNewPassword1(e.target.value)}
                                                placeholder="Enter your new password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '12px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: '#718096'
                                                }}
                                            >
                                                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                        {newPassword1 && (
                                            <div className={`Account-Settings-Password-Strength strength-${passwordStrength}`}>
                                                <div className='Account-Settings-Strength-Bar'>
                                                    <div className='Account-Settings-Strength-Fill'></div>
                                                </div>
                                                <div className='Account-Settings-Strength-Text'>
                                                    Password strength: {getPasswordStrengthText()}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className='Account-Settings-Field'>
                                        <label className='Account-Settings-Label'>Confirm New Password</label>
                                        <div style={{ position: 'relative' }}>
                                            <input 
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                className='Account-Settings-Input'
                                                value={newPassword2}
                                                onChange={(e) => setNewPassword2(e.target.value)}
                                                placeholder="Confirm your new password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '12px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: '#718096'
                                                }}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>

                                    {noMatchWarning && (
                                        <div className='Account-Settings-Message Account-Settings-Warning'>
                                            Passwords do not match.
                                        </div>
                                    )}

                                    {wrongPasswordWarning && (
                                        <div className='Account-Settings-Message Account-Settings-Warning'>
                                            Current password is not correct.
                                        </div>
                                    )}

                                    {successfulPasswordChange && (
                                        <div className='Account-Settings-Message Account-Settings-Success'>
                                            Password has been updated successfully. You will be logged out in a moment.
                                        </div>
                                    )}

                                    <div className='Account-Settings-Button-Group'>
                                        <button 
                                            className='Account-Settings-Button Account-Settings-Button-Primary'
                                            onClick={updatePassword}
                                            disabled={!clickAble}
                                        >
                                            Update Password
                                        </button>
                                        <button 
                                            className='Account-Settings-Button Account-Settings-Button-Secondary'
                                            onClick={resetPasswordForm}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Privileges Card */}
                    <div className='Account-Settings-Card'>
                        <div className='Account-Settings-Card-Header'>
                            <FaShieldAlt className='Account-Settings-Card-Icon' />
                            <h2 className='Account-Settings-Card-Title'>System Privileges</h2>
                        </div>
                        
                        <div className='Account-Settings-Privileges'>
                            {privileges.map((privilege) => (
                                <div 
                                    key={privilege.key}
                                    className={`Account-Settings-Priv-Item ${activeUser[privilege.key] ? 'enabled' : ''}`}
                                >
                                    {activeUser[privilege.key] ? (
                                        <ImCheckboxChecked className='Account-Settings-Priv-Icon enabled' />
                                    ) : (
                                        <ImCheckboxUnchecked className='Account-Settings-Priv-Icon' />
                                    )}
                                    <span className='Account-Settings-Priv-Title'>
                                        {privilege.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default AccountSettings;